import { useEffect, useState } from "react";
import moment from "moment";
import { AssignmentAgingConfig, VehicleAgingConfig } from "../config/redux/common/commonSlice.types";

const CHECK_INTERVAL_MS = 30000;
const DEFAULT_VEHICLE = "bike";

const resolveVehicleConfig = (
    config: AssignmentAgingConfig,
    storeCode?: string,
    vehicle?: string
): VehicleAgingConfig | undefined => {
    const vehicleKey = (vehicle || DEFAULT_VEHICLE).toLowerCase();
    if (config.useStoreLevelConfig && storeCode) {
        const storeScope = config.stores?.[storeCode];
        // vehicle not configured for this store -> try the store's own bike
        // default before falling through to the global defaultConfig
        const storeConfig = storeScope?.[vehicleKey] || storeScope?.[DEFAULT_VEHICLE];
        if (storeConfig) {
            return storeConfig;
        }
    }
    // no store-level config at all for this store/vehicle (or store-level
    // config is off, or defaultConfig itself is missing/malformed since
    // `config` comes from JSON.parse on a BE string with no runtime shape
    // guarantee) -> fall through to defaultConfig
    return config.defaultConfig?.[vehicleKey] || config.defaultConfig?.[DEFAULT_VEHICLE];
};

const getAssignmentAgingColor = (
    config: AssignmentAgingConfig | undefined,
    storeCode?: string,
    vehicle?: string,
    assignmentDate?: string
): string | undefined => {
    if (!assignmentDate || !config) {
        return undefined;
    }
    const thresholds = resolveVehicleConfig(config, storeCode, vehicle)?.delayThresholds;
    if (!thresholds?.length) {
        return undefined;
    }

    // assignmentDate arrives from the backend as a local wall-clock string
    // with no offset (AppConstants.DATE_FORMAT_BACKEND), already in the
    // viewer's timezone (matches how other DATE_FORMAT_BACKEND fields are
    // parsed, e.g. transactionDate in mocks/express/response.transforms.ts).
    // Parsing with moment.utc() would shift it by the local UTC offset and
    // produce a bogus (even negative) diff.
    const elapsedMinutes = moment().diff(moment(assignmentDate), "minutes", true);

    // thresholds are walked ascending so the highest one reached wins
    let color: string | undefined;
    [...thresholds]
        .sort((a, b) => a.delayMinutes - b.delayMinutes)
        .forEach((threshold) => {
            if (elapsedMinutes >= threshold.delayMinutes) {
                color = threshold.colorHex;
            }
        });
    return color;
};

// Colors a driver name per the store/vehicle-specific delay thresholds in
// `config`, re-checking on an interval since the elapsed time keeps
// advancing even without new data from the server.
export const useAssignmentAgingColor = (
    config: AssignmentAgingConfig | undefined,
    storeCode?: string,
    vehicle?: string,
    assignmentDate?: string
): string | undefined => {
    const [color, setColor] = useState<string | undefined>(() =>
        getAssignmentAgingColor(config, storeCode, vehicle, assignmentDate)
    );

    useEffect(() => {
        setColor(getAssignmentAgingColor(config, storeCode, vehicle, assignmentDate));

        if (!assignmentDate) {
            return;
        }

        const intervalId = setInterval(() => {
            setColor(getAssignmentAgingColor(config, storeCode, vehicle, assignmentDate));
        }, CHECK_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [config, storeCode, vehicle, assignmentDate]);

    return color;
};
